import Vue from "vue";
import Router, { NavigationGuardNext, Route } from "vue-router";
//import multiguard from "vue-router-multiguard";

Vue.use(Router);

/**
 * Layouts
 */
import SiteLayout from "./components/_site/SiteLayout.vue";
import AppLayout from "./AppLayout.vue";

/**
 * Components
 */
const PageNotFound = Vue.component("page-not-found", {
	created: function () {
		window.location.href = "/";
	},
	template: ""
});

import ErrorPage from "@/components/_global/ErrorPage.vue";

/**
 * Utils
 */
import role, { roles } from "@/config/roles";

/**
 * API
 */
import teamsAPI from "@/api/teams";

/**
 * VueX
 */
import store from "@/store";

/**
 * Before enter
 */
import { requireAuth } from "@/utils/auth";

const mustBeAdmin = () => {
	return async (to: Route, from: Route, next: NavigationGuardNext) => {
		const currentUser = to.meta.userProfile;
		if (!(currentUser && currentUser.isAdmin)) {
			return next({
				path: "/teams"
			});
		}
		return next();
	};
};


const mustBeInTeam = () => {
	return async (to: Route, from: Route, next: NavigationGuardNext) => {
		const teamSlug = to.params.teamSlug;
		const teamRegion = to.params.teamRegion;
		const teamRealmSlug = to.params.teamRealmSlug;
		const teamNameSlug = to.params.teamNameSlug;

		try {
			let teamInfos = null;
			if (teamSlug != null) {
				teamInfos = await teamsAPI.getTeamInfosBySlug(teamSlug);
			}
			else if (teamRegion != null && teamRealmSlug != null && teamNameSlug != null) {
				teamInfos = await teamsAPI.getTeamInfosBySlugs(teamRegion, teamRealmSlug, teamNameSlug);
			}
			to.meta.teamInfos = teamInfos;
		}
		catch (error) {
			await store.dispatch("team/fetch", null);
			return next({
				path: "/teams"
			});
		}

		return next();
	};
};

const mustHavePermission = (permission: number) => {
	return async (to: Route, from: Route, next: NavigationGuardNext) => {
		try {
			const teamInfos = to.meta.teamInfos;

			const hasPermission = role(teamInfos.access.permissions).hasPermission(permission);
			if (!hasPermission) {
				return next({
					path: "/teams"
				});
			}
		}
		catch (error) {
			await store.dispatch("team/fetch", null);
			return next({
				path: "/teams"
			});
		}

		return next();
	};
};
//
// const mustBeAGuild = () => {
// 	return async (to: any, from: any, next: any) => {
// 		const teamInfos = to.meta.teamInfos;
// 		if (teamInfos.guildNameSlug == null) {
// 			return next({
// 				path: "/teams"
// 			});
// 		}
// 		return next();
// 	};
// };

const teamRoutes = [
	{
		path: "dashboard",
		component: () => import("./components/dashboard/DashboardPage.vue"),
		children: [
			{
				path: "overview/:rosterId?",
				component: () => import("./components/dashboard/DashboardRosterSubPage.vue"),
			},
			{
				path: "wishes/:rosterId?",
				component: () => import("./components/dashboard/WishesPage.vue"),
			},
			{
				path: "vault/:rosterId?",
				component: () => import("./components/dashboard/GreatVaultPage.vue"),
			},
			{
				path: "gear/:rosterId?",
				component: () => import("./components/dashboard/GearPage.vue"),
			},
		]
	},
	{
		path: "calendar",
		component: () => import("./components/calendar/CalendarPage.vue")
	},
	{
		path: "events/:id",
		component: () => import("./components/composition/EventPage.vue"),
		children: [
			{
				path: "compositions",
				component: () => import("./components/composition/CompositionsPage.vue"),
				children: [
					{
						path: "",
						component: () => import("./components/composition/CompositionsViewPage.vue")
					},
					{
						path: "compare",
						component: () => import("./components/composition/CompositionsComparePage.vue")
					},
					{
						path: "edit",
						component: () => import("./components/composition/CompositionsEditorPage.vue"),
						beforeEnter: mustHavePermission(roles.MANAGE_COMPOSITIONS)
					},
					{
						path: "distribution",
						component: () => import("./components/composition/CompositionsViewDistributionPage.vue")
					},
					{
						path: "wide",
						component: () => import("./components/composition/CompositionsWideViewPage.vue")
					},
				]
			},
			{
				path: "signups",
				component: () => import("./components/composition/SignupsViewPage.vue")
			},

		]
	},

	{
		path: "rosters",
		component: () => import("./components/roster/ListPage.vue"),
		beforeEnter: mustHavePermission(roles.MANAGE_ROSTERS)
	},
	{
		path: "rosters/edit",
		component: () => import("./components/roster/EditorPage.vue"),
		beforeEnter: mustHavePermission(roles.MANAGE_ROSTERS)
	},
	{
		path: "characters",
		component: () => import("./components/team/TeamCharactersPage.vue"),
		beforeEnter: mustHavePermission(roles.MANAGE_TEAM)
	},
	{
		path: "members",
		component: () => import("./components/team/TeamMembersPage.vue"),
		beforeEnter: mustHavePermission(roles.MANAGE_TEAM)
	},
	{
		path: "settings",
		component: () => import("./components/team/SettingsPage.vue"),
		beforeEnter: mustHavePermission(roles.MANAGE_DISCORD)
	},
	{
		path: "premium",
		component: () => import("./components/team/PremiumPage.vue"),
	},
	{
		path: "",
		redirect: "dashboard"
	},
];


const router = new Router({
	mode: "history",
	base: process.env.BASE_URL,
	scrollBehavior: function (to, from, savedPosition) {
		return { x: 0, y: 0 };
	},
	routes: [
		{
			path: "/",
			component: SiteLayout,
			children: [
				{
					path: "/",
					name: "home",
					component: () => import("./components/authentication/LoginPage.vue"),
				},
				{
					path: "login",
					name: "login",
					component: () => import("./components/authentication/LoginPage.vue"),
				},
				{
					path: "privacy",
					name: "privacy",
					component: () => import("./components/_site/PrivacyPage.vue"),
				},
				{
					path: "terms",
					name: "terms",
					component: () => import("./components/_site/TermsPage.vue"),
				}]
		},
		{
			path: "/error",
			component: ErrorPage
		},
		{
			path: "/invite/:inviteCode",
			beforeEnter: requireAuth(),
			component: () => import("./components/authentication/InviteTeamPage.vue")
		},
		{
			path: "/",
			component: AppLayout,
			beforeEnter: requireAuth(),
			children: [
				{
					path: "profile",
					component: () => import("./components/user/UserPage.vue"),
					children: [
						{
							path: "",
							component: () => import("./components/user/ProfilePage.vue")
						},
						{
							path: "premium",
							component: () => import("./components/user/PremiumPage.vue")
						},
						{
							path: "keystones",
							component: () => import("./components/user/CharactersMythicKeystonesPage.vue")
						},
					]
				},

				{
					path: "t/:teamSlug",
					component: () => import("./AppTeamLayout.vue"),
					beforeEnter: mustBeInTeam(),
					children: teamRoutes
				},
				{
					path: ":teamRegion/:teamRealmSlug/:teamNameSlug",
					component: () => import("./AppTeamLayout.vue"),
					beforeEnter: mustBeInTeam(),
					children: teamRoutes
				},
				{
					path: "teams",
					component: () => import("./components/authentication/TeamsListPage.vue")
				},
				{
					path: "admin",
					component: () => import("./components/admin/AdminLayout.vue"),
					beforeEnter: mustBeAdmin(),
					children: [
						{
							path: "users",
							component: () => import("./components/admin/CharactersListPage.vue")
						},
						{
							path: "teams",
							component: () => import("./components/admin/TeamsListPage.vue")
						},
						{
							path: "sync",
							component: () => import("./components/admin/SyncQueueListPage.vue")
						},
					]

				},
				{
					path: "characters/:id",
					component: () => import("./components/character/WishesEditorPage.vue")
				}
			]
		},
		{
			path: "*",
			component: PageNotFound
		}
	]
});

router.beforeEach(async (to: Route, from: Route, next: NavigationGuardNext) => {
	to.meta.userProfile = from.meta.userProfile;
	to.meta.teamInfos = from.meta.teamInfos;
	return next();
});

export default router;
